import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import type { ApiClient } from '../api/client';
import ErrorBanner from '../components/ErrorBanner';
import type {
  AppAccessSummary,
  AppRole,
  ProjectCapabilityDefinition,
  ProjectInvitation,
  ProjectMember
} from '../types';
import { canManageMembers, effectiveAccessSummary, invitationBrowserPath } from '../utils/projectAccess';
import { reportErrorLocal } from '../utils/errors';
import './AppMembersPage.lane35.css';

type Props = {
  apiClient: ApiClient;
  setError: (message: string | null) => void;
  access?: AppAccessSummary | null;
};

type InvitationCreateResponse = {
  invitation: ProjectInvitation;
  acceptance_token: string;
  acceptance_path: string;
};

function isRole(value: string): value is AppRole {
  return value === 'owner' || value === 'contributor' || value === 'viewer';
}

function toLocalDateTimeInput(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

export default function AppMembersPage({ apiClient, setError, access: suppliedAccess }: Props) {
  const appId = useParams().appId ?? '';
  const [access, setAccess] = useState<AppAccessSummary | null>(suppliedAccess ?? null);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [capabilities, setCapabilities] = useState<ProjectCapabilityDefinition[]>([]);
  const [invitations, setInvitations] = useState<ProjectInvitation[]>([]);
  const [expiryDrafts, setExpiryDrafts] = useState<Record<string, string>>({});
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'contributor' | 'viewer'>('contributor');
  const [expiresInDays, setExpiresInDays] = useState('7');
  const [pageError, setPageError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copiedInvitationId, setCopiedInvitationId] = useState<string | null>(null);
  // Plaintext invitation tokens are intentionally kept only in this page's memory.
  const [acceptanceLinks, setAcceptanceLinks] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      const nextAccess = suppliedAccess ?? await apiClient.get<AppAccessSummary>(`/api/apps/${appId}/access`);
      setAccess(nextAccess);
      if (!canManageMembers(nextAccess)) {
        return;
      }
      const [memberResult, invitationResult, capabilityResult] = await Promise.all([
        apiClient.get<{ members?: ProjectMember[] } | ProjectMember[]>(`/api/apps/${appId}/members`),
        apiClient.get<{ invitations?: ProjectInvitation[] } | ProjectInvitation[]>(`/api/apps/${appId}/invitations`),
        apiClient.get<ProjectCapabilityDefinition[]>(`/api/apps/${appId}/capabilities`)
      ]);
      const nextMembers = Array.isArray(memberResult) ? memberResult : memberResult.members ?? [];
      setMembers(nextMembers);
      setExpiryDrafts(Object.fromEntries(nextMembers.map((member) => [
        member.id,
        member.status === 'active' ? toLocalDateTimeInput(member.expires_at) : ''
      ])));
      setInvitations(Array.isArray(invitationResult) ? invitationResult : invitationResult.invitations ?? []);
      setCapabilities(capabilityResult);
    } catch (err) {
      reportErrorLocal('Failed to load project members', err, setError, setPageError);
    }
  }, [apiClient, appId, setError, suppliedAccess]);

  useEffect(() => {
    void load();
  }, [load]);

  async function createInvitation(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      setPageError('An exact email address is required.');
      return;
    }
    const parsedExpiry = Number.parseInt(expiresInDays, 10);
    setBusy(true);
    setPageError(null);
    setNotice(null);
    try {
      const response = await apiClient.post<InvitationCreateResponse>(`/api/apps/${appId}/invitations`, {
        email: normalizedEmail,
        role,
        ...(Number.isFinite(parsedExpiry) && parsedExpiry > 0 ? { expires_in_days: parsedExpiry } : {})
      });
      setInvitations((previous) => [response.invitation, ...previous]);
      setEmail('');
      const path = invitationBrowserPath(response.acceptance_path, response.acceptance_token);
      const link = new URL(path, window.location.origin).toString();
      setAcceptanceLinks((previous) => ({ ...previous, [response.invitation.id]: link }));
      setNotice(`Invitation created. Copy this one-time acceptance link: ${link}`);
      try {
        await navigator.clipboard?.writeText(link);
        setCopiedInvitationId(response.invitation.id);
      } catch {
        // Browsers may deny clipboard access; the visible link remains copyable.
      }
    } catch (err) {
      reportErrorLocal('Failed to invite member', err, setError, setPageError);
    } finally {
      setBusy(false);
    }
  }

  async function copyAcceptanceLink(invitation: ProjectInvitation) {
    const path = acceptanceLinks[invitation.id] ?? invitation.acceptance_path;
    if (!path) {
      setPageError('The acceptance link is shown only when an invitation is created. Create a new invitation to issue a new one-time link.');
      return;
    }
    try {
      await navigator.clipboard?.writeText(path.startsWith('http') ? path : new URL(path, window.location.origin).toString());
      setCopiedInvitationId(invitation.id);
      setNotice('Acceptance link copied.');
    } catch (err) {
      reportErrorLocal('Failed to copy acceptance link', err, setError, setPageError);
    }
  }

  async function changeRole(member: ProjectMember, nextRole: string) {
    if (!isRole(nextRole) || nextRole === member.role || member.status !== 'active') return;
    if (!window.confirm(`Change ${member.email}'s role to ${nextRole}?`)) return;
    setBusy(true);
    setPageError(null);
    try {
      const updated = await apiClient.patch<ProjectMember>(`/api/apps/${appId}/members/${member.id}`, { role: nextRole });
      setMembers((previous) => previous.map((item) => item.id === member.id ? updated : item));
    } catch (err) {
      reportErrorLocal('Failed to change member role', err, setError, setPageError);
    } finally {
      setBusy(false);
    }
  }

  async function removeMember(member: ProjectMember) {
    if (!window.confirm(`Remove ${member.email} from this project? Their new access will be revoked immediately.`)) return;
    setBusy(true);
    setPageError(null);
    try {
      await apiClient.del(`/api/apps/${appId}/members/${member.id}`);
      await load();
    } catch (err) {
      reportErrorLocal('Failed to remove member', err, setError, setPageError);
    } finally {
      setBusy(false);
    }
  }

  async function saveExpiry(member: ProjectMember, clear: boolean) {
    const draft = expiryDrafts[member.id] ?? '';
    if (!clear && !draft) {
      setPageError('Choose an expiry date and time, or select No expiry.');
      return;
    }
    const parsed = draft ? new Date(draft) : null;
    if (!clear && (!parsed || Number.isNaN(parsed.getTime()) || parsed <= new Date())) {
      setPageError('Membership expiry must be in the future.');
      return;
    }
    setBusy(true);
    setPageError(null);
    try {
      const updated = await apiClient.patch<ProjectMember>(`/api/apps/${appId}/members/${member.id}`, clear
        ? { clear_expires_at: true }
        : { expires_at: parsed?.toISOString() });
      setMembers((previous) => previous.map((item) => item.id === member.id ? updated : item));
      setExpiryDrafts((previous) => ({ ...previous, [member.id]: toLocalDateTimeInput(updated.expires_at) }));
      setNotice(clear ? 'Membership is now non-expiring.' : 'Membership expiry updated.');
    } catch (err) {
      reportErrorLocal('Failed to change membership expiry', err, setError, setPageError);
    } finally {
      setBusy(false);
    }
  }

  async function reactivateMember(member: ProjectMember) {
    const draft = expiryDrafts[member.id] ?? '';
    const parsed = draft ? new Date(draft) : null;
    if (parsed && (Number.isNaN(parsed.getTime()) || parsed <= new Date())) {
      setPageError('Reactivation expiry must be in the future.');
      return;
    }
    if (!window.confirm(`Reactivate ${member.email}${parsed ? ` until ${parsed.toLocaleString()}` : ' without an expiry'}?`)) return;
    setBusy(true);
    setPageError(null);
    try {
      const updated = await apiClient.patch<ProjectMember>(`/api/apps/${appId}/members/${member.id}`, {
        reactivate: true,
        ...(parsed ? { expires_at: parsed.toISOString() } : { clear_expires_at: true })
      });
      setMembers((previous) => previous.map((item) => item.id === member.id ? updated : item));
      setExpiryDrafts((previous) => ({ ...previous, [member.id]: toLocalDateTimeInput(updated.expires_at) }));
      setNotice(`${member.email} was reactivated. Historical exceptional grants were not restored.`);
    } catch (err) {
      reportErrorLocal('Failed to reactivate member', err, setError, setPageError);
    } finally {
      setBusy(false);
    }
  }

  async function changeCapabilityGrant(member: ProjectMember, capability: ProjectCapabilityDefinition, effect: string) {
    const encodedCapability = encodeURIComponent(capability.name);
    setBusy(true);
    setPageError(null);
    try {
      if (effect === 'inherit') {
        await apiClient.del(`/api/apps/${appId}/members/${member.id}/capability-grants/${encodedCapability}`);
        await load();
      } else if (effect === 'allow' || effect === 'deny') {
        const updated = await apiClient.put<ProjectMember>(
          `/api/apps/${appId}/members/${member.id}/capability-grants/${encodedCapability}`,
          { effect }
        );
        setMembers((previous) => previous.map((item) => item.id === member.id ? updated : item));
      }
      setNotice(`Custom access for ${capability.name} updated.`);
    } catch (err) {
      reportErrorLocal('Failed to change custom access', err, setError, setPageError);
    } finally {
      setBusy(false);
    }
  }

  async function revokeInvitation(invitation: ProjectInvitation) {
    if (!window.confirm(`Revoke the invitation for ${invitation.email}?`)) return;
    setBusy(true);
    setPageError(null);
    try {
      await apiClient.del(`/api/apps/${appId}/invitations/${invitation.id}`);
      setInvitations((previous) => previous.filter((item) => item.id !== invitation.id));
    } catch (err) {
      reportErrorLocal('Failed to revoke invitation', err, setError, setPageError);
    } finally {
      setBusy(false);
    }
  }

  if (!access) return <div className="card">Loading project access…</div>;
  if (!canManageMembers(access)) {
    return <div className="card"><h2>Members</h2><p>You do not have permission to manage project members.</p></div>;
  }

  return (
    <section className="grid project-members-page members-lane35">
      <div className="card full">
        <div className="card-header members-lane35__header">
          <div>
            <p className="members-lane35__eyebrow">Project access</p>
            <h2>Project members</h2>
            <p className="status">Effective access is enforced by the server. Roles are a starting template, not a bypass for sensitive actions.</p>
          </div>
          <Link className="link-button" to={`/apps/${appId}`}>Back to project</Link>
        </div>
        {pageError && <ErrorBanner message={pageError} onDismiss={() => setPageError(null)} />}
        {notice && <div className="notification notification--success"><span>{notice}</span><button type="button" className="ghost compact" onClick={() => setNotice(null)} aria-label="Dismiss notification">×</button></div>}

        <form className="project-members-invite" onSubmit={createInvitation} aria-labelledby="members-lane35-invite">
          <h3 id="members-lane35-invite">Invite a collaborator</h3>
          <p className="status">Use the recipient’s exact sign-in email. The one-time acceptance link is only returned now.</p>
          <div className="members-lane35__invite-fields">
            <label><span>Email address</span>
            <input aria-label="Email address" type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="person@example.com" required />
            </label>
            <label><span>Invitation role</span>
            <select aria-label="Invitation role" value={role} onChange={(event) => setRole(event.target.value as 'contributor' | 'viewer')}>
              <option value="contributor">Contributor</option>
              <option value="viewer">Viewer</option>
            </select>
            </label>
            <label><span>Expires in days</span>
            <input aria-label="Invitation expiry in days" type="number" min="1" value={expiresInDays} onChange={(event) => setExpiresInDays(event.target.value)} />
            </label>
            <button type="submit" disabled={busy}>Create invitation</button>
          </div>
          <p className="hint">{effectiveAccessSummary(role, [])}</p>
        </form>

        <section className="members-lane35__section" aria-labelledby="members-lane35-active">
        <h3 id="members-lane35-active">Active members</h3>
        <p className="status">Expiry and custom access changes invalidate prepared runs immediately. A deny overrides both the role template and a custom allow.</p>
        <div className="responsive-table-scroll">
          <table className="responsive-table project-members-table" aria-labelledby="members-lane35-active">
            <thead><tr><th>Member</th><th>Role</th><th>Expiry</th><th>Effective access</th><th>Actions</th></tr></thead>
            <tbody>
              {members.filter((member) => member.status === 'active').map((member) => {
                const grantByCapability = new Map(member.capability_grants.map((grant) => [grant.capability, grant.effect]));
                return (
                  <React.Fragment key={member.id}>
                    <tr>
                      <td data-label="Member"><strong>{member.name ?? member.email}</strong><br /><span className="text-muted">{member.email}</span><br /><span className="badge badge--success">active</span></td>
                      <td data-label="Role">
                        <select aria-label={`Role for ${member.email}`} value={member.role} disabled={busy} onChange={(event) => void changeRole(member, event.target.value)}>
                          <option value="owner">Owner</option>
                          <option value="contributor">Contributor</option>
                          <option value="viewer">Viewer</option>
                        </select>
                        <div className="hint">Template v{member.role_template_version} · membership v{member.membership_version}</div>
                      </td>
                      <td data-label="Expiry">
                        <div className="project-member-expiry">
                          <input
                            aria-label={`Expiry for ${member.email}`}
                            type="datetime-local"
                            value={expiryDrafts[member.id] ?? ''}
                            disabled={busy}
                            onChange={(event) => setExpiryDrafts((previous) => ({ ...previous, [member.id]: event.target.value }))}
                          />
                          <div className="row">
                            <button type="button" className="ghost compact" disabled={busy || !(expiryDrafts[member.id] ?? '')} onClick={() => void saveExpiry(member, false)}>Set</button>
                            <button type="button" className="ghost compact" disabled={busy || !member.expires_at} onClick={() => void saveExpiry(member, true)}>No expiry</button>
                          </div>
                        </div>
                      </td>
                      <td data-label="Effective access" className="text-muted">{effectiveAccessSummary(member.role, member.capabilities ?? [])}</td>
                      <td data-label="Actions"><button type="button" className="danger secondary" disabled={busy} onClick={() => void removeMember(member)}>Revoke access</button></td>
                    </tr>
                    <tr className="project-member-custom-row">
                      <td colSpan={5}>
                        <details className="project-member-custom-access">
                          <summary>Advanced custom access ({member.capability_grants.length} override{member.capability_grants.length === 1 ? '' : 's'})</summary>
                          <p className="status">Use role defaults for the common case. Custom allows cannot delegate governance, settings, secret-management, model-management, budget, executor-management, or release execution.</p>
                          <div className="project-capability-grid">
                            {capabilities.map((capability) => {
                              const selected = grantByCapability.get(capability.name) ?? 'inherit';
                              const effective = member.capabilities.includes(capability.name);
                              return (
                                <label key={capability.name} className="project-capability-control">
                                  <span><code>{capability.name}</code>{capability.sensitive && <span className="badge badge--warning">sensitive</span>}<small>{capability.description}</small></span>
                                  <span className={effective ? 'badge badge--success' : 'badge'}>{effective ? 'effective' : 'not granted'}</span>
                                  <select
                                    aria-label={`Custom access ${capability.name} for ${member.email}`}
                                    value={selected}
                                    disabled={busy}
                                    onChange={(event) => void changeCapabilityGrant(member, capability, event.target.value)}
                                  >
                                    <option value="inherit">Role default</option>
                                    <option value="allow" disabled={!capability.custom_allow_permitted}>Custom allow</option>
                                    <option value="deny">Explicit deny</option>
                                  </select>
                                </label>
                              );
                            })}
                          </div>
                        </details>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
              {members.every((member) => member.status !== 'active') && <tr><td colSpan={5}>No active members found.</td></tr>}
            </tbody>
          </table>
        </div>
        </section>

        <section className="members-lane35__section" aria-labelledby="members-lane35-history">
        <h3 id="members-lane35-history">Membership history</h3>
        <p className="status">Revoked memberships remain as audit history. Reactivation starts from the selected role template and never restores revoked custom grants.</p>
        <div className="responsive-table-scroll">
        <table className="responsive-table" aria-labelledby="members-lane35-history">
          <thead><tr><th>Member</th><th>Status</th><th>Role</th><th>Last active / expiry</th><th>Actions</th></tr></thead>
          <tbody>
            {members.filter((member) => member.status !== 'active').map((member) => (
              <tr key={member.id}>
                <td data-label="Member"><strong>{member.name ?? member.email}</strong><br /><span className="text-muted">{member.email}</span></td>
                <td data-label="Status"><span className={`badge ${member.status === 'expired' ? 'badge--warning' : 'badge--error'}`}>{member.status}</span></td>
                <td data-label="Role">{member.role}<div className="hint">membership v{member.membership_version}</div></td>
                <td data-label="Expiry">
                  <input
                    aria-label={`Reactivation expiry for ${member.email}`}
                    type="datetime-local"
                    value={expiryDrafts[member.id] ?? ''}
                    disabled={busy}
                    onChange={(event) => setExpiryDrafts((previous) => ({ ...previous, [member.id]: event.target.value }))}
                  />
                  <div className="hint">{member.expires_at ? `Previously expired ${new Date(member.expires_at).toLocaleString()}` : member.revoked_at ? `Revoked ${new Date(member.revoked_at).toLocaleString()}` : '—'}</div>
                </td>
                <td data-label="Actions"><button type="button" className="secondary" disabled={busy} onClick={() => void reactivateMember(member)}>Reactivate</button></td>
              </tr>
            ))}
            {members.every((member) => member.status === 'active') && <tr><td colSpan={5}>No revoked or expired memberships.</td></tr>}
          </tbody>
        </table>
        </div>
        </section>

        <section className="members-lane35__section" aria-labelledby="members-lane35-pending">
        <h3 id="members-lane35-pending">Pending invitations</h3>
        <p className="status">Copy link is available only while the one-time link is held on this page. Revoking an invitation cancels that invitation, not an existing membership.</p>
        <div className="responsive-table-scroll">
        <table className="responsive-table" aria-labelledby="members-lane35-pending">
          <thead><tr><th>Email</th><th>Role</th><th>Expires</th><th>Actions</th></tr></thead>
          <tbody>
            {invitations.filter((invitation) => invitation.status === 'pending').map((invitation) => (
              <tr key={invitation.id}>
                <td data-label="Email">{invitation.email}</td><td data-label="Role"><span className="badge">{invitation.role}</span></td>
                <td data-label="Expires">{invitation.expires_at ? new Date(invitation.expires_at).toLocaleString() : '—'}</td>
                <td data-label="Actions"><div className="members-lane35__invitation-actions">
                  <button type="button" className="ghost" disabled={busy || !(acceptanceLinks[invitation.id] ?? invitation.acceptance_path)} onClick={() => void copyAcceptanceLink(invitation)}>{copiedInvitationId === invitation.id ? 'Copied' : 'Copy link'}</button>
                  <button type="button" className="danger secondary" disabled={busy} onClick={() => void revokeInvitation(invitation)}>Revoke invitation</button>
                </div></td>
              </tr>
            ))}
            {invitations.filter((invitation) => invitation.status === 'pending').length === 0 && <tr><td colSpan={4}>No pending invitations.</td></tr>}
          </tbody>
        </table>
        </div>
        </section>
      </div>
    </section>
  );
}
